import classNames from "classnames";
import { forwardRef } from "react";

interface PiBrowseListRowProps {
  icon: React.ReactNode;
  title: string;
  /**
   * Replaces the title line while it is set — an inline rename field. The row
   * keeps its geometry, so nothing shifts when the edit starts.
   */
  titleField?: React.ReactNode;
  /** One line under the title — a description string or a row of chips. */
  subtitle?: React.ReactNode;
  /** Extra content at the end of the row — version tags, category chips. */
  trailing?: React.ReactNode;
  /**
   * Per-item actions, typically a popover menu. Kept in its own slot because
   * it swallows its own clicks: activating the menu must not select or confirm
   * the row underneath it.
   */
  menu?: React.ReactNode;
  /** Target for the search field's aria-activedescendant. */
  optionId?: string;
  isSelected: boolean;
  /**
   * Present but not choosable — e.g. a video in an images-only explorer. The
   * row's own content dims; the `menu` deliberately does not, because the
   * whole reason these rows stay on screen is that you can still act on them.
   */
  isDisabled?: boolean;
  onSelect: () => void;
  onConfirm: () => void;
}

/**
 * Selectable row for the browse dialogs' list view. Inset 8px from the list
 * edges so the selection tint reads as a deliberate shape rather than a
 * full-bleed band; the treatment lives here so it cannot drift between
 * dialogs. The selection tint is the only marker the row carries — a per-row
 * ↵ glyph repeated the footer button's Enter affordance on every line, which
 * read as noise rather than help.
 *
 * A div rather than a button: the `menu` slot puts a real button inside the
 * row, and a button inside a button is invalid. Keyboard never reaches the row
 * anyway — the dialog's shared list hook owns the arrows, and the row is
 * deliberately out of the tab order.
 */
const PiBrowseListRow = forwardRef<HTMLDivElement, PiBrowseListRowProps>(
  function PiBrowseListRow(props, ref) {
    const {
      icon,
      title,
      titleField,
      subtitle,
      trailing,
      menu,
      optionId,
      isSelected,
      isDisabled = false,
      onSelect,
      onConfirm,
    } = props;

    // One class, applied to each content span rather than the row, so the
    // menu can sit at full strength inside a dimmed row.
    const dim = isDisabled ? "opacity-45" : undefined;

    return (
      <div
        ref={ref}
        id={optionId}
        role="option"
        aria-selected={isSelected}
        aria-disabled={isDisabled || undefined}
        tabIndex={-1}
        onClick={onSelect}
        onDoubleClick={onConfirm}
        className={classNames(
          "group w-[calc(100%-16px)] mx-[8px] flex items-center gap-[12px] px-[10px] py-[8px] rounded-[8px] text-left select-none transition-colors",
          isSelected && "bg-creator-primary-washed",
          !isSelected && !isDisabled && "hover:bg-creator-childActive",
          isDisabled && "cursor-not-allowed"
        )}
      >
        <span
          className={classNames(
            "w-[32px] h-[32px] shrink-0 rounded-[6px] border flex items-center justify-center text-[17px]",
            isSelected
              ? "border-creator-primary text-creator-text-key"
              : "border-creator-border text-creator-text-sub",
            dim
          )}
        >
          {icon}
        </span>

        <span className={classNames("min-w-0 flex-1", dim)}>
          {titleField ?? (
            <span className="block text-[13px] leading-[18px] text-creator-text truncate">
              {title}
            </span>
          )}
          {subtitle && (
            <span className="block text-[11px] leading-[16px] text-creator-text-sub truncate">
              {subtitle}
            </span>
          )}
        </span>

        {trailing && (
          <span
            className={classNames(
              "shrink-0 flex items-center gap-[6px]",
              dim
            )}
          >
            {trailing}
          </span>
        )}

        {menu && (
          <span
            className="shrink-0 flex items-center"
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            {menu}
          </span>
        )}
      </div>
    );
  }
);

export default PiBrowseListRow;
